// frontend/src/utils/crsLabels.js

/**
 * Finds the label for a given value in an options array.
 * @param {Array} options - The array of option objects (e.g., [{ value: 'A', label: 'Option A' }]).
 * @param {*} value - The value to find the label for.
 * @returns {string|*} - The corresponding label or the original value if not found or if options is invalid.
 */
const findLabel = (options, value) => {
    // Guard against non-array options to prevent runtime errors.
    if (!Array.isArray(options)) {
        // Log an error for debugging purposes to identify which option list is problematic.
        console.error("Invalid options provided to findLabel. Expected an array, but received:", options);
        // Return the original value as a safe fallback.
        return value;
    }
    const option = options.find(opt => opt.value === value);
    // If a matching option is found, return its label. Otherwise, return the original value.
    return option ? option.label : value;
};

/**
 * Maps formData from its internal code representation to a human-readable format for saving.
 * @param {Object} formData - The raw form data with code values.
 * @param {Object} allOptions - An object containing all the option arrays used in the form.
 * @returns {Object} - A new object with human-readable labels.
 */
export const mapFormDataToLabels = (formData, allOptions) => {
    const {
        maritalStatusOptions,
        ageOptions,
        educationLevelOptions,
        canadianEducationLevelOptions,
        languageTestOptions,
        languageScoreOptions,
        canadianWorkExperienceOptions,
        foreignWorkExperienceOptions,
        yesNoOptions,
        jobOfferNocOptions
    } = allOptions;

    const labeledData = {
        maritalStatus: findLabel(maritalStatusOptions, formData.maritalStatus),
        age: findLabel(ageOptions, formData.age),
        educationLevel: findLabel(educationLevelOptions, formData.educationLevel),
        hasCanadianEducation: findLabel(yesNoOptions, formData.hasCanadianEducation),
        canadianEducationLevel: findLabel(canadianEducationLevelOptions, formData.canadianEducationLevel),
        
        firstLanguageTest: findLabel(languageTestOptions, formData.firstLanguageTest),
        firstLanguageScores: {
            speaking: findLabel(languageScoreOptions[formData.firstLanguageTest]?.speaking, formData.firstLanguageScores.speaking),
            listening: findLabel(languageScoreOptions[formData.firstLanguageTest]?.listening, formData.firstLanguageScores.listening),
            reading: findLabel(languageScoreOptions[formData.firstLanguageTest]?.reading, formData.firstLanguageScores.reading),
            writing: findLabel(languageScoreOptions[formData.firstLanguageTest]?.writing, formData.firstLanguageScores.writing),
        },

        secondLanguageTest: findLabel(languageTestOptions, formData.secondLanguageTest),
        secondLanguageScores: {
            speaking: findLabel(languageScoreOptions[formData.secondLanguageTest]?.speaking, formData.secondLanguageScores.speaking),
            listening: findLabel(languageScoreOptions[formData.secondLanguageTest]?.listening, formData.secondLanguageScores.listening),
            reading: findLabel(languageScoreOptions[formData.secondLanguageTest]?.reading, formData.secondLanguageScores.reading),
            writing: findLabel(languageScoreOptions[formData.secondLanguageTest]?.writing, formData.secondLanguageScores.writing),
        },

        canadianWorkExperience: findLabel(canadianWorkExperienceOptions, formData.canadianWorkExperience),
        foreignWorkExperience: findLabel(foreignWorkExperienceOptions, formData.foreignWorkExperience),

        hasJobOffer: findLabel(yesNoOptions, formData.hasJobOffer),
        jobOfferNoc: findLabel(jobOfferNocOptions, formData.jobOfferNoc),
        hasPnpNomination: findLabel(yesNoOptions, formData.hasPnpNomination),
        hasSiblingInCanada: findLabel(yesNoOptions, formData.hasSiblingInCanada),
        hasCertificateOfQualification: findLabel(yesNoOptions, formData.hasCertificateOfQualification),
    };

    // Add spouse factors only if they are relevant
    if (formData.maritalStatus === 'B') { // 'B' corresponds to Married or Common-law
        labeledData.spouseIsPR = findLabel(yesNoOptions, formData.spouseIsPR);
        labeledData.spouseWillAccompany = findLabel(yesNoOptions, formData.spouseWillAccompany);
        
        // Only add detailed spouse factors if they will accompany
        if (formData.spouseWillAccompany === 'A') { // 'A' is 'Yes'
            labeledData.spouseEducationLevel = findLabel(educationLevelOptions, formData.spouseEducationLevel);
            labeledData.spouseCanadianWorkExperience = findLabel(canadianWorkExperienceOptions, formData.spouseCanadianWorkExperience);
            labeledData.spouseLanguageTest = findLabel(languageTestOptions, formData.spouseLanguageTest);
            labeledData.spouseLanguageScores = {
                speaking: findLabel(languageScoreOptions[formData.spouseLanguageTest]?.speaking, formData.spouseLanguageScores.speaking),
                listening: findLabel(languageScoreOptions[formData.spouseLanguageTest]?.listening, formData.spouseLanguageScores.listening),
                reading: findLabel(languageScoreOptions[formData.spouseLanguageTest]?.reading, formData.spouseLanguageScores.reading),
                writing: findLabel(languageScoreOptions[formData.spouseLanguageTest]?.writing, formData.spouseLanguageScores.writing),
            };
        }
    }

    // Clean up null values from fields that were not applicable (e.g. second language test)
    Object.keys(labeledData).forEach(key => {
        if (labeledData[key] === null || labeledData[key] === 'Not applicable') {
            labeledData[key] = 'N/A';
        }
        if (typeof labeledData[key] === 'object' && labeledData[key] !== null) {
             Object.keys(labeledData[key]).forEach(subKey => {
                if (labeledData[key][subKey] === null || labeledData[key][subKey] === 'Not applicable') {
                    labeledData[key][subKey] = 'N/A';
                }
             });
        }
    });

    return labeledData;
};